import React, { useState, useEffect } from 'react';
import { CalendarDaysIcon, MegaphoneIcon, NewspaperIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';

interface Newsletter {
  id: string;
  title: string;
  date: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  lastModified: string;
}

const Newsletters: React.FC = () => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNewsletters();
  }, []);

  const loadNewsletters = async () => {
    setLoading(true);
    try {
      const data = await apiService.getNewsletters();
      const newsletters = Array.isArray(data) ? data.filter(item => item !== null) as unknown as Newsletter[] : [];
      setNewsletters(newsletters);
    } catch (error) {
      console.error('Failed to load newsletters:', error);
      setNewsletters([]);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-church-light to-white">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-church-dark mb-4">
              Parish Newsletters
            </h1>
            <div className="w-24 h-1 bg-church-gold mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Stay connected with our parish community through our weekly newsletters
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue mx-auto mb-4"></div>
                <p className="text-gray-600">Loading newsletters...</p>
              </div>
            ) : newsletters.length === 0 ? (
              <div className="text-center py-12">
                <NewspaperIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Newsletters Available</h3>
                <p className="text-gray-600">
                  Check back soon for our latest parish newsletters and updates.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold text-church-dark mb-4">
                    Available Newsletters
                  </h2>
                  <p className="text-gray-600">
                    Click on any newsletter below to view or download the PDF.
                  </p>
                </div>

                <div className="grid gap-6">
                  {newsletters.map((newsletter) => (
                    <div key={newsletter.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-3">
                            <NewspaperIcon className="h-6 w-6 text-church-blue mr-3" />
                            <h3 className="text-xl font-semibold text-church-dark">
                              {newsletter.title}
                            </h3>
                          </div>
                          
                          <div className="flex items-center text-gray-600 mb-4 space-x-6">
                            <div className="flex items-center">
                              <CalendarDaysIcon className="h-4 w-4 mr-2" />
                              <span className="text-sm">
                                {formatDate(newsletter.date)}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <MegaphoneIcon className="h-4 w-4 mr-2" />
                              <span className="text-sm">
                                {formatFileSize(newsletter.fileSize)}
                              </span>
                            </div>
                          </div>

                          <p className="text-gray-700 text-sm mb-4">
                            Filename: {newsletter.fileName}
                          </p>
                        </div>

                        <div className="ml-6 flex flex-col space-y-2">
                          <a
                            href={newsletter.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-church-blue hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 text-center"
                          >
                            View PDF
                          </a>
                          <a
                            href={newsletter.fileUrl}
                            download={newsletter.fileName}
                            className="bg-church-gold hover:bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 text-center"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-church-light rounded-lg">
                  <h3 className="text-lg font-semibold text-church-dark mb-3">
                    About Our Newsletters
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our parish newsletters are published weekly and contain important updates about 
                    upcoming events, parish news, spiritual reflections, and community announcements. 
                    They are an excellent way to stay connected with the life of our parish community.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    If you would like to receive our newsletter by email or have content to contribute, 
                    please contact the parish office at{' '}
                    <a href="mailto:allsaintsthirsk@rcdmidd.org.uk" className="text-church-blue hover:underline">
                      allsaintsthirsk@rcdmidd.org.uk
                    </a>{' '}
                    or call us at{' '}
                    <a href="tel:01845527011" className="text-church-blue hover:underline">
                      01845 527011
                    </a>.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Newsletters;